<template>
  <fragment>
    <h1 class="main-title mb-4 d-flex align-center">
      <span class="main-title__text">
        {{ $t("dashboard.productSampling.title") }}
      </span>
    </h1>
    <v-row class="mx-0">
      <p v-if="product" class="wx-typo-h3 mb-0">{{ getProductDisplayName(product) }}</p>
      <v-row class="mx-0 mb-2 mt-0" justify="end">
        <p class="ma-0">
          <span class="font-weight-medium">{{ $t("common.date") }}: </span>
          <span>{{ formattedDate }}</span>
        </p>
        <v-divider vertical class="mx-5" />
        <p class="ma-0">
          <span class="font-weight-medium">{{ $t("common.time") }}: </span>
          <span>{{ formattedTime }}</span>
        </p>
      </v-row>
    </v-row>
    <v-row class="mx-0" justify="end">
      <p class="wx-typo-sm text--disabled" v-if="samplingSessionInProgress">
        {{ $t("common.sessionId") }} : {{ samplingSessionInProgress.session_id }}
      </p>
      <v-spacer />
      <p>
        {{ $t("dashboard.productSampling.sessionExpire") }}:
        <span class="ml-5 font-weight-medium" :class="timerColor">{{ formattedTimer }}</span>
      </p>
    </v-row>
    <samples
      ref="samples"
      v-if="product"
      :sample-count="sampleCount"
      :product="product"
      :sampling-type="samplingType"
      @cancel="cancelSession"
      @submit="completeSession"
      @samplesCompleted="completeSamples"
      @expired="expiredSession"
    />
    <instructions v-if="samplingType === 'assisted'" />
    <session-expired-dialog :show="sessionExpired" @back="back" @restart="restart" />
    <confirm-cancel-dialog :show="cancellingSession" @cancel="cancel" @back="() => (cancellingSession = false)" />
    <confirm-submit-dialog
      v-if="sampleCount"
      :show="submittingSession"
      :expected-sample-count="sampleCount"
      :missing-sample-count="missingSampleCount"
      @submit="submit"
      @back="() => (submittingSession = false)"
    />
  </fragment>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ProductService from "@/components/product/ProductService";
import SessionExpiredDialog from "@/components/productsamplingsession/dialogs/SessionExpiredDialog.vue";
import ConfirmCancelDialog from "@/components/productsamplingsession/dialogs/ConfirmCancelDialog.vue";
import Instructions from "@/components/productsamplingsession/Instructions";
import Samples from "@/components/productsamplingsession/Samples";
import GiveawaySamplingService from "@/components/productsamplingsession/GiveawaySamplingService";
import RouteService from "@/router/RouteService";
import ConfirmSubmitDialog from "@/components/productsamplingsession/dialogs/ConfirmSubmitDialog.vue";
import ErrorHandling from "@/components/ErrorHandling";

const TIME_PER_SAMPLE = 120;

export default {
  name: "ProductSamplingView",
  components: {
    ConfirmSubmitDialog,
    SessionExpiredDialog,
    Instructions,
    Samples,
    ConfirmCancelDialog,
  },
  data() {
    return {
      product: null,
      now: null,
      timer: TIME_PER_SAMPLE,
      sampleCount: null,
      samplesCompleted: false,
      sessionExpired: false,
      cancellingSession: false,
      submittingSession: false,
      providedSampleCount: 0,
      comment: null,

      timerInterval: null,
      timezone: null,
    };
  },
  watch: {
    timer() {
      if (this.timer <= 0) {
        clearInterval(this.timerInterval);
        if (this.$refs.samples) {
          this.$refs.samples.clearFetchSamplesInterval();
        }
        this.sessionExpired = true;
      }
    },
  },
  computed: {
    ...mapGetters("dashboard", ["activeProductionUnit", "samplingSessionInProgress", "activeProductionUnitId"]),
    ...mapGetters("navigation", ["activeFactory"]),
    formattedTimer() {
      if (this.samplesCompleted) return "-";

      const minutes = parseInt(this.timer / 60);
      const seconds = parseInt(this.timer % 60);
      const minutesStr = minutes >= 10 ? `${minutes}` : `0${minutes}`;
      const secondsStr = seconds >= 10 ? `${seconds}` : `0${seconds}`;
      return `${minutesStr}:${secondsStr}`;
    },
    timerColor() {
      return this.timer > 30 || this.samplesCompleted ? null : "red-timer";
    },
    localeDateString() {
      if (!this.now || !this.timezone) return null;

      return this.now.toLocaleString("en-CA", {
        timezone: this.timezone,
        hour12: false,
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      });
    },
    formattedDate() {
      if (!this.localeDateString) return "";

      return this.localeDateString.split(", ")[0];
    },
    formattedTime() {
      if (!this.localeDateString) return "";

      return this.localeDateString.split(", ")[1];
    },
    samplingType() {
      return this.$route.params.samplingType;
    },
    missingSampleCount() {
      return this.sampleCount - this.providedSampleCount;
    },
  },
  methods: {
    ...mapActions("dashboard", ["setSamplingSessionInProgress"]),
    ...mapActions("operation", ["handleHttpError", "showOperationSuccess"]),
    ...mapActions("user", ["updateProductionUnitSamplingType"]),
    ...mapActions("operation", ["showOperationError"]),
    completeSamples() {
      this.samplesCompleted = true;
      clearInterval(this.timerInterval);
      this.$refs.samples.clearFetchSamplesInterval();
    },
    cancelSession(cancellationData) {
      this.cancellingSession = true;
      if (cancellationData && cancellationData.comment) {
        this.comment = cancellationData.comment;
      }
    },
    completeSession(completionData) {
      this.submittingSession = true;
      this.providedSampleCount = completionData.providedSampleCount;
      if (completionData && completionData.comment) {
        this.comment = completionData.comment;
      }
    },
    submit() {
      GiveawaySamplingService.completeSamplingSession(
        this.activeProductionUnitId,
        this.samplingSessionInProgress.session_id,
        this.comment,
      )
        .then(() => {
          this.updateProductionUnitSamplingType(this.samplingType);
          this.showOperationSuccess(this.$t("dashboard.productSampling.successfulSampling"));
          this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
        })
        .catch((error) => this.handleCompleteSamplingSessionError(error.response));
    },
    fetchProduct() {
      ProductService.getProduct(this.$route.params.id, false)
        .then((httpResponse) => {
          this.product = httpResponse.data;
        })
        .catch((httpError) => {
          this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
          this.handleHttpError(httpError);
        });
    },
    startTimer() {
      const initialTimer = (this.samplingSessionInProgress.end_time - this.samplingSessionInProgress.start_time) / 1000;
      const adjustedTimer = (Date.now() - this.samplingSessionInProgress.start_time) / 1000;
      this.timer = initialTimer - adjustedTimer;
      this.timerInterval = setInterval(() => {
        this.timer--;
      }, 1000);
    },
    restart() {
      // create a new session and reset variables
      GiveawaySamplingService.createSamplingSession(
        this.activeProductionUnitId,
        { sku: this.samplingSessionInProgress.sku },
        this.samplingType,
      ).then((httpResponse) => {
        this.setSamplingSessionInProgress(httpResponse.data.resource);
        this.now = new Date(this.samplingSessionInProgress.start_time);
        this.sessionExpired = false;
        this.samplesCompleted = false;
        this.$refs.samples.startFetchSamplesInterval();
        this.startTimer();
      });
    },
    cancel() {
      GiveawaySamplingService.cancelSamplingSession(
        this.activeProductionUnitId,
        this.samplingSessionInProgress.session_id,
        this.comment,
      )
        .then(() => {
          this.setSamplingSessionInProgress(null);
          this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
        })
        .catch((error) => this.handleCancelSamplingSessionError(error.response));
    },
    back() {
      this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
    },
    getProductDisplayName(product) {
      let productDescriptionString = product.description ? " (" + product.description + ")" : "";
      return product.sku + productDescriptionString;
    },
    handleCompleteSamplingSessionError(httpResponse) {
      let errorMessage = ErrorHandling.buildErrorsMessages(httpResponse, this.getCompleteSamplingSessionErrorMessage);
      if (errorMessage) {
        this.showOperationError(errorMessage);
      } else {
        this.setSamplingSessionInProgress(null);
        this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
      }
    },
    getCompleteSamplingSessionErrorMessage(code) {
      switch (code) {
        case "CS_GS_PUT_10002":  // Deprecated
        case "DSH_GSS_PUT_10002":
          return null;
        default:
          return this.$t("common.errors.default", { code: code });
      }
    },
    handleCancelSamplingSessionError(httpResponse) {
      let errorMessage = ErrorHandling.buildErrorsMessages(httpResponse, this.getCancelSamplingSessionErrorMessage);
      if (errorMessage) this.showOperationError(errorMessage);
      else {
        this.setSamplingSessionInProgress(null);
        this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
      }
    },
    getCancelSamplingSessionErrorMessage(code) {
      switch (code) {
        case "CS_GS_DELETE_10002":  // Deprecated
        case "DSH_GSS_DELETE_10002":
          return null;
        default:
          return this.$t("common.errors.default", { code: code });
      }
    },
    expiredSession() {
      clearInterval(this.timerInterval);
      if (this.$refs.samples) {
        this.$refs.samples.clearFetchSamplesInterval();
      }
      this.sessionExpired = true;
    },
  },
  mounted() {
    if (!this.activeProductionUnit || !this.activeFactory || !this.samplingSessionInProgress) {
      // this means the page was refreshed
      return this.$router.replace(RouteService.toDashboard(this.activeProductionUnitId, "giveaway"));
    }
    this.sampleCount = this.activeProductionUnit.giveaway_sampling_configuration.sample_count;
    this.timezone = this.activeFactory.timezone;
    this.now = new Date(this.samplingSessionInProgress.start_time);
    this.fetchProduct();
    this.startTimer();
  },
  beforeDestroy() {
    // clear the interval before destroying the component
    clearInterval(this.timerInterval);
  },
};
</script>

<style lang="scss" scoped>
.red-timer {
  color: red;
}
</style>
